// Room roster, game/review history, and lecture activity panels.
import type { Color, Role, RoomState } from '../../types';
import { MoveHistory } from '../MoveHistory';
import { ReviewHistoryPanel } from '../review/ReviewHistoryPanel';

type RoomActivitySidebarProps = {
  role: Role;
  roomId: string;
  state: RoomState | null;
  review: boolean;
  reviewHistory: RoomState['history'];
  studentList: Record<string, string>;
  studentMoveHistory: Array<{ studentName: string; move: string; isCorrect: boolean }>;
  studentEvents: string[];
  attempts: string[];
  onSelectReview: (entry: RoomState['history'][number], index: number) => void;
};

function firstMoveColor(entries: RoomState['history']): Color {
  const nextTurn = entries[0]?.fen.split(/\s+/)[1];
  return nextTurn === 'w' ? 'b' : 'w';
}

export function RoomActivitySidebar({
  role,
  roomId,
  state,
  review,
  reviewHistory,
  studentList,
  studentMoveHistory,
  studentEvents,
  attempts,
  onSelectReview
}: RoomActivitySidebarProps) {
  return (
    <>
      {role === 'student' && (
        <section className="panel">
          <div className="room-summary">
            <div className="room-code">{roomId}</div>
            <div className="muted">Вы — ученик</div>
          </div>
        </section>
      )}

      {state?.mode !== 'lecture' && state?.history.length ? (
        <section className="panel">
          <h2>Ходы партии</h2>
          <MoveHistory entries={state.history} firstMoveColor={firstMoveColor(state.history)} />
        </section>
      ) : null}

      {role === 'trainer' && review && (
        <ReviewHistoryPanel entries={reviewHistory} interactive onSelect={onSelectReview} />
      )}

      {role === 'student' && state?.mode === 'lecture' && state.reviewHistory?.length ? (
        <ReviewHistoryPanel entries={reviewHistory} />
      ) : null}

      {role === 'trainer' && (
        <section className="panel">
          <div className="room-summary">
            <div className="room-code">{roomId}</div>
            <div className="muted">Вы — тренер</div>
          </div>
          <h2>Ученики ({Object.keys(studentList).length})</h2>
          <div className="events">
            {Object.entries(studentList).length
              ? Object.entries(studentList).map(([id, studentName]) => <div key={id}>● {studentName}</div>)
              : <div>Пока никого нет</div>}
          </div>
        </section>
      )}

      {role === 'trainer' && state?.mode === 'lecture' && (
        <section className="panel">
          <h2>История ходов</h2>
          <div className="events">
            {studentMoveHistory.length
              ? studentMoveHistory.map((item, index) => (
                  <div key={index} className={item.isCorrect ? 'student-move-correct' : 'student-move-incorrect'}>
                    {item.isCorrect ? '✓ ' : '✗ '}{item.studentName}: {item.move}
                  </div>
                ))
              : <div>Пока нет ходов</div>}
          </div>
        </section>
      )}

      {role === 'trainer' && studentEvents.length > 0 && (
        <section className="panel">
          <h2>События учеников</h2>
          <div className="events">{studentEvents.map((event, index) => <div key={index}>{event}</div>)}</div>
        </section>
      )}

      {role === 'student' && state?.mode === 'lecture' && attempts.length > 0 && (
        <section className="panel">
          <h2>Мои попытки</h2>
          <div className="events">
            {attempts.map((event, index) => (
              <div key={index} className={event.startsWith('✓ ') ? 'student-move-correct' : 'student-move-incorrect'}>{event}</div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
